import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { privateRoutes } from "../routes/routeConfig";
import Portal from "./Portal";
import { HiChevronDown, HiChevronRight, HiOutlineChevronDoubleLeft, HiOutlineChevronDoubleRight } from "react-icons/hi";
import { Images } from "../assets/images";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { setIsCollapsed } from "../redux/slices/commonSlice";

const SIDEBAR_PORTAL_ID = "sidebar-popover-root";

const Sidebar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const dispatch = useDispatch();
    const sidebarRef = useRef<HTMLDivElement | null>(null);

    const { collapsed } = useSelector((state: RootState) => state.common);

    const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
    const [openPopoverMenu, setOpenPopoverMenu] = useState<string | null>(null);
    const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        setOpenPopoverMenu(null);
        setPopoverPos(null);
    }, [navigationType]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node | null;
            const portalRoot = document.getElementById(SIDEBAR_PORTAL_ID);

            const clickedInsideSidebar = sidebarRef.current?.contains(target ?? null);
            const clickedInsidePortal = portalRoot?.contains(target ?? null);

            if (clickedInsideSidebar || clickedInsidePortal) return;

            setOpenPopoverMenu(null);
            setPopoverPos(null);
        };

        if (openPopoverMenu) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [openPopoverMenu]);

    useEffect(() => {
        setExpandedMenus([]);
        setOpenPopoverMenu(null);
        setPopoverPos(null);
    }, [collapsed]);

    // 🚨 Updated to use menuKey for state tracking, and only navigate if path exists
    const handleMainClick = (
        menuKey: string,
        hasSubRoutes: boolean,
        path?: string,
        event?: React.MouseEvent | React.KeyboardEvent
    ) => {
        if (!collapsed) {
            if (hasSubRoutes) {
                setExpandedMenus((prev) =>
                    prev.includes(menuKey) ? prev.filter((p) => p !== menuKey) : [...prev, menuKey]
                );
            } else if (path) {
                navigate(path);
            }
            return;
        }

        if (!hasSubRoutes) {
            if (path) navigate(path);
            return;
        }

        let rect: DOMRect | null = null;
        if (event && (event.currentTarget as Element)) {
            rect = (event.currentTarget as Element).getBoundingClientRect();
        } else {
            const el = document.querySelector(`[data-menu="${CSS.escape(menuKey)}"]`) as HTMLElement | null;
            rect = el?.getBoundingClientRect() ?? null;
        }

        if (openPopoverMenu === menuKey) {
            setOpenPopoverMenu(null);
            setPopoverPos(null);
        } else {
            const top = rect ? rect.top : 64;
            const left = rect ? rect.right + 12 : collapsed ? 80 : 300;
            setOpenPopoverMenu(menuKey);
            setPopoverPos({ top, left });
        }
    };

    // 🚨 Updated to correctly highlight parent menus only when their children are active
    const isRouteActive = (path?: string, subRoutes?: typeof privateRoutes) => {
        if (subRoutes) {
            return subRoutes.some((sr) => {
                if (location.pathname === sr.path) return true;
                if (sr.path && sr.path !== "/" && location.pathname.startsWith(`${sr.path}/`)) {
                    return true;
                }
                return false;
            });
        }
        return path ? location.pathname === path : false;
    };

    return (
        <aside
            ref={sidebarRef}
            className={`bg-light_primary flex flex-col h-screen transition-all duration-300 ease-in-out relative border-r border-borderColor shadow-sm ${
                collapsed ? "w-20" : "w-64"
            }`}
        >
            <div className="flex items-center justify-center h-20 mb-4 shrink-0 border-b border-borderColor/50">
                <img
                    src={collapsed ? Images.icon_logo : Images.logo_horizantal}
                    alt="Logo"
                    className={`object-contain transition-all duration-300 ${collapsed ? "h-10 w-10" : "h-10 max-w-[180px]"}`}
                />
            </div>

            <nav className="flex-1 flex flex-col gap-1 overflow-y-auto overflow-x-hidden scrollbar-hide px-3 pb-24 relative z-20">
                {privateRoutes
                    .filter((route) => route.label && route.icon)
                    .map(({ path, label, subRoutes, icon: Icon }) => {
                        const menuKey = path || label || "unknown"; // 🚨 Use label as ID if path doesn't exist
                        const hasSubRoutes = !!(subRoutes && subRoutes.length > 0);
                        const isExpanded = expandedMenus.includes(menuKey);
                        const isPopoverOpen = openPopoverMenu === menuKey;
                        const isActive = isRouteActive(path, subRoutes);

                        return (
                            <div
                                key={menuKey}
                                className="relative"
                                data-menu={menuKey}
                                aria-expanded={hasSubRoutes ? isExpanded : undefined}
                                aria-controls={hasSubRoutes ? `submenu-${menuKey}` : undefined}
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => handleMainClick(menuKey, hasSubRoutes, path, e)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            handleMainClick(menuKey, hasSubRoutes, path);
                                        }
                                    }}
                                    className={`group flex items-center justify-between px-3 py-2.5 mb-1 rounded-xl cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                                        isActive
                                            ? "bg-primary text-cardBackground shadow-md font-semibold"
                                            : "text-headingText hover:bg-primary/10 hover:text-primary"
                                    } ${collapsed ? "justify-center" : ""}`}
                                >
                                    <div className="flex items-center gap-3 shrink-0">
                                        {Icon && (
                                            <Icon
                                                size={20}
                                                className={`transition-colors ${
                                                    isActive 
                                                    ? "text-cardBackground" 
                                                    : "text-primary group-hover:text-primary"
                                                }`}
                                            />
                                        )}
                                        {!collapsed && <span className="whitespace-nowrap tracking-wide text-sm">{label}</span>}
                                    </div>
                                    {hasSubRoutes && !collapsed && (
                                        <span aria-hidden="true" className={isActive ? "text-cardBackground/80" : "text-primary/60"}>
                                            {isExpanded ? <HiChevronDown size={18} /> : <HiChevronRight size={18} />}
                                        </span>
                                    )}
                                </div>

                                {hasSubRoutes && isExpanded && !collapsed && (
                                    <div id={`submenu-${menuKey}`} className="ml-7 pl-3 border-l-2 border-primary/20 flex flex-col gap-1 mt-1 mb-3" role="region">
                                        {subRoutes!.map(({ path: subPath, label: subLabel, icon: SubIcon }) => {
                                            const subIsActive = location.pathname === subPath;
                                            return (
                                                <button
                                                    key={subPath}
                                                    onClick={() => subPath && navigate(subPath)}
                                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 flex items-center gap-3 ${
                                                        subIsActive
                                                            ? "bg-primary/10 text-primary font-bold"
                                                            : "text-headingText/80 hover:bg-primary/5 hover:text-primary"
                                                    }`}
                                                >
                                                    {SubIcon && (
                                                        <SubIcon
                                                            size={16}
                                                            className={subIsActive ? "text-primary" : "text-primary/70"}
                                                        />
                                                    )}
                                                    <span className="truncate">{subLabel}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {hasSubRoutes && isPopoverOpen && collapsed && popoverPos && (
                                    <Portal>
                                        <div
                                            className="fixed bg-cardBackground rounded-xl shadow-xl z-50 p-2 w-52 border border-borderColor overflow-hidden animate-in fade-in slide-in-from-left-2 duration-200"
                                            style={{ top: popoverPos.top, left: popoverPos.left }}
                                            role="menu"
                                        >
                                            <div className="px-3 py-2 mb-1 border-b border-borderColor">
                                                <p className="text-xs font-bold text-primary uppercase tracking-wider">{label}</p>
                                            </div>
                                            {subRoutes!.map(({ path: subPath, label: subLabel, icon: SubIcon }) => {
                                                const subIsActive = location.pathname === subPath;
                                                return (
                                                    <button
                                                        key={subPath}
                                                        onClick={() => {
                                                            if (subPath) navigate(subPath);
                                                            setOpenPopoverMenu(null);
                                                            setPopoverPos(null);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 mt-1 ${
                                                            subIsActive
                                                                ? "bg-primary text-cardBackground font-bold"
                                                                : "text-headingText hover:bg-primary/10 hover:text-primary"
                                                        }`}
                                                        role="menuitem"
                                                    >
                                                        {SubIcon && (
                                                            <SubIcon
                                                                size={16}
                                                                className={subIsActive ? "text-cardBackground" : "text-primary"}
                                                            />
                                                        )}
                                                        <span>{subLabel}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </Portal>
                                )}
                            </div>
                        );
                    })}
            </nav>
        </aside>
    );
};

export default Sidebar;